import { type RefObject } from 'react'

import type { TimeValue } from '@graftcode/design-system'

import { CustomRangeSection } from '../TimeRangeFilter/components/CustomRangeSection'
import type {
  ServiceMapCustomRange,
  ServiceMapTimeRangeFilterOption,
  ServiceMapTimeRangeFilterValue,
} from '../TimeRangeFilter/time-range.types'

import { FilterSheetMenuItem } from './components/FilterSheetMenuItem'
import { SheetSelectRow } from './SheetSelectRow'

import styles from './FiltersMobileSheet.module.scss'

type TimeRangeLabels = {
  title: string
  startDate: string
  endDate: string
  startTime: string
  endTime: string
}

type GatewayListMobileTimeRangeFilterSectionProps = {
  isOpen: boolean
  customGroupRef: RefObject<HTMLDivElement | null>
  timeRangeLabels: TimeRangeLabels
  timeRangeTriggerLabel: string
  openMenu: boolean
  onMenuOpenChange: (open: boolean) => void
  menuItems: {
    regular: ServiceMapTimeRangeFilterOption[]
    custom: ServiceMapTimeRangeFilterOption[]
  }
  pendingTimeRange: ServiceMapTimeRangeFilterValue
  onSelectTimeRange: (value: ServiceMapTimeRangeFilterValue) => void
  showCustom: boolean
  pendingCustomRange: ServiceMapCustomRange
  onStartDateChange: (value: Date | null) => void
  onStartTimeChange: (value: TimeValue | null) => void
  onEndDateChange: (value: Date | null) => void
  onEndTimeChange: (value: TimeValue | null) => void
  menuItemTestIdPrefix?: string
  sheetRootTestId?: string
}

export function GatewayListMobileTimeRangeFilterSection({
  isOpen,
  customGroupRef,
  timeRangeLabels,
  timeRangeTriggerLabel,
  openMenu,
  onMenuOpenChange,
  menuItems,
  pendingTimeRange,
  onSelectTimeRange,
  showCustom,
  pendingCustomRange,
  onStartDateChange,
  onStartTimeChange,
  onEndDateChange,
  onEndTimeChange,
  menuItemTestIdPrefix = 'gateway-list-filter-sheet-time',
  sheetRootTestId,
}: GatewayListMobileTimeRangeFilterSectionProps) {
  const renderTimeRangeMenuItem = (option: ServiceMapTimeRangeFilterOption) => (
    <FilterSheetMenuItem
      key={option.value}
      label={option.label}
      isSelected={pendingTimeRange === option.value}
      onSelect={() => onSelectTimeRange(option.value as ServiceMapTimeRangeFilterValue)}
      testId={`${menuItemTestIdPrefix}-${option.value}`}
    />
  )

  return (
    <div className={styles.rowGroup} data-testid={isOpen ? sheetRootTestId : undefined}>
      <SheetSelectRow
        rowLabel={timeRangeLabels.title}
        displayValue={timeRangeTriggerLabel}
        menuOpen={openMenu}
        onMenuOpenChange={onMenuOpenChange}
      >
        <div className={styles.menuItems}>
          {menuItems.regular.map(renderTimeRangeMenuItem)}
          {menuItems.custom.length > 0 ? (
            <>
              <div className={styles.menuDivider} aria-hidden="true" />
              {menuItems.custom.map(renderTimeRangeMenuItem)}
            </>
          ) : null}
        </div>
      </SheetSelectRow>

      {showCustom ? (
        <div className={styles.customGroup} ref={customGroupRef}>
          <CustomRangeSection
            startDate={pendingCustomRange.startDate}
            startTime={pendingCustomRange.startTime}
            endDate={pendingCustomRange.endDate}
            endTime={pendingCustomRange.endTime}
            labels={{
              startDate: timeRangeLabels.startDate,
              endDate: timeRangeLabels.endDate,
              startTime: timeRangeLabels.startTime,
              endTime: timeRangeLabels.endTime,
            }}
            hour12
            layout="mobile"
            onStartDateChange={onStartDateChange}
            onStartTimeChange={onStartTimeChange}
            onEndDateChange={onEndDateChange}
            onEndTimeChange={onEndTimeChange}
          />
        </div>
      ) : null}
    </div>
  )
}
